import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import type { RegistrationRequestListItemDto } from "RegistrationRequests/Api/registrationRequestApi";
import { RegistrationRequestStatusChip } from "RegistrationRequests/Components/RegistrationRequestStatusChip";
import { RegistrationRequestTypeChip } from "RegistrationRequests/Components/RegistrationRequestTypeChip";
import { DetailActionCell } from "Shared/DataTable/DetailActionCell";
import { NameLinkCell } from "Shared/NameLinkCell";
import { DateFormat, formatDate } from "Shared/Utils/Format/date";

const columnHelper = createColumnHelper<RegistrationRequestListItemDto>();

export const REGISTRATION_REQUEST_SORTABLE_COLUMNS = [
  "name",
  "type",
  "status",
  "dateCreated",
] as const;

const isSortable = (id: string) =>
  (REGISTRATION_REQUEST_SORTABLE_COLUMNS as readonly string[]).includes(id);

export function useRegistrationRequestsTableColumns() {
  const { t } = useResource();

  return useMemo(
    () => [
      columnHelper.accessor((row) => `${row.firstName} ${row.lastName}`, {
        id: "name",
        header: t(Resources.RegistrationRequests.Name),
        enableSorting: isSortable("name"),
        cell: (info) => (
          <NameLinkCell
            path={AppRouting.RegistrationRequestDetail.path(
              info.row.original.registrationRequestID,
            )}
          >
            {info.getValue()}
          </NameLinkCell>
        ),
      }),
      columnHelper.accessor("type", {
        header: t(Resources.RegistrationRequests.Type),
        enableSorting: isSortable("type"),
        cell: (info) => <RegistrationRequestTypeChip type={info.getValue()} />,
      }),
      columnHelper.accessor("company", {
        header: t(Resources.RegistrationRequests.Company),
        enableSorting: false,
        cell: (info) => info.getValue() ?? "-",
      }),
      columnHelper.accessor("status", {
        header: t(Resources.RegistrationRequests.Status),
        enableSorting: isSortable("status"),
        cell: (info) => (
          <RegistrationRequestStatusChip status={info.getValue()} />
        ),
      }),
      columnHelper.accessor("dateCreated", {
        header: t(Resources.RegistrationRequests.DateCreated),
        enableSorting: isSortable("dateCreated"),
        cell: (info) => formatDate(info.getValue(), DateFormat.DateTime),
      }),
      columnHelper.display({
        id: "action",
        header: "",
        enableSorting: false,
        meta: { maxWidth: 50, justifySelf: "end" },
        cell: (info) => (
          <DetailActionCell
            path={AppRouting.RegistrationRequestDetail.path(
              info.row.original.registrationRequestID,
            )}
          />
        ),
      }),
    ],
    [t],
  );
}
